import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Input } from './ui/input';
import { downloadReceiptPdf, apiJson, messageFrom } from '../lib/account';
import RatingBox from './RatingBox';
import CancelRideBox from './CancelRideBox';
import { safeRides, shortDate, num, str } from '../lib/safe';
import { MapPin, ArrowLeft, Car, Search, Heart, User, Loader2 } from 'lucide-react';
import { BOOK_PATH } from '../lib/routes';

const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";

const statusDot = (status) => {
  switch (status) {
    case 'completed': return 'bg-emerald-400';
    case 'pending': return 'bg-orange-400';
    case 'in-progress': return 'bg-sky-400';
    case 'cancelled': return 'bg-red-400';
    default: return 'bg-gray-500';
  }
};

const isPaid = (ride) => ['paid', 'captured'].includes((ride.paymentStatus || '').toLowerCase());

const RideHistory = () => {
  const navigate = useNavigate();
  const { getUserRides, addTip, toggleFavoriteDriver, currentUser } = useAuth();
  const { toast } = useToast();
  const [rides, setRides] = useState([]);
  const [filteredRides, setFilteredRides] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [tippingId, setTippingId] = useState(null);
  const [favBusy, setFavBusy] = useState(null);
  const [receiptBusy, setReceiptBusy] = useState(null);

  const handleRated = (ride, rating) => {
    setRides((prev) => prev.map((r) => (r.id === ride.id ? { ...r, rating } : r)));
    toast({ title: 'Thanks for rating', description: 'Your chauffeur will see it as part of their ratings.' });
  };

  const handleCancelled = (ride) => {
    setRides((prev) => prev.map((r) => (r.id === ride.id ? { ...r, status: 'cancelled' } : r)));
  };

  const handleReceipt = async (ride, mode) => {
    const ref = ride.reference || ride.id;
    setReceiptBusy(`${mode}:${ride.id}`);
    try {
      if (mode === 'pdf') {
        await downloadReceiptPdf(ref);
      } else {
        const r = await apiJson(`/api/rides/${encodeURIComponent(ref)}/email-receipt`, 'POST');
        if (!r.ok || !r.body.ok) throw new Error(messageFrom(r.body, 'We could not send the receipt. Please try again.'));
        toast({ title: 'Receipt sent', description: `We emailed the receipt to ${currentUser?.email || 'you'}.` });
      }
    } catch (err) {
      toast({ title: 'Receipt', description: err.message || 'Something went wrong.', variant: 'destructive' });
    } finally {
      setReceiptBusy(null);
    }
  };

  const favoriteDrivers = Array.isArray(currentUser?.favoriteDrivers) ? currentUser.favoriteDrivers : [];
  const defaultTipPct = parseInt(str(currentUser?.preferences?.tip || '18%').replace('%', ''), 10) || 18;

  useEffect(() => {
    const userRides = safeRides(getUserRides());          // whatever the server sent, made safe to show
    setRides(userRides);
    setFilteredRides(userRides);
  }, [getUserRides]);

  const handleTip = async (ride, pct) => {
    const amount = Math.round((Number(ride.estimatedFare || 0) * pct) / 100 * 100) / 100;
    setTippingId(ride.id);
    try {
      await addTip(ride.id, amount, pct);
      setRides((prev) => prev.map((r) => (r.id === ride.id ? { ...r, tipAmount: amount, tipPercent: pct } : r)));
      toast({ title: 'Gratuity added', description: `$${amount.toFixed(2)} for your chauffeur — thank you.`, duration: 3000 });
    } catch (e) {
      toast({ title: 'Could not add gratuity', description: e.message, variant: 'destructive' });
    } finally {
      setTippingId(null);
    }
  };

  const handleFavorite = async (driver) => {
    setFavBusy(driver);
    try {
      await toggleFavoriteDriver(driver);
    } catch (e) {
      toast({ title: 'Could not update favorites', description: e.message, variant: 'destructive' });
    } finally {
      setFavBusy(null);
    }
  };

  useEffect(() => {
    let filtered = rides;
    if (searchTerm) {
      filtered = filtered.filter((ride) =>
        str(ride.pickupAddress).toLowerCase().includes(searchTerm.toLowerCase()) ||
        str(ride.dropoffAddress).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter((ride) => ride.status === filterStatus);
    }
    setFilteredRides(filtered);
  }, [rides, searchTerm, filterStatus]);

  const formatDate = (dateString) => shortDate(dateString);

  const totalSpent = rides.reduce((sum, r) => sum + num(r.estimatedFare), 0);

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] lg:w-[800px] lg:h-[800px] rounded-full bg-orange-600/15 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 right-0 w-[360px] h-[360px] rounded-full bg-orange-500/10 blur-[120px]" />

      <div className="relative w-full px-5 sm:px-6 lg:px-10 2xl:px-16 pb-16">
        <header className="flex items-center justify-between py-6 lg:py-8">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/dashboard')}
            className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:border-orange-500/40 active:scale-95 transition-all"
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="logo-home"
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 active:scale-95 transition-transform"
            aria-label="Home"
          >
            <img src={LOGO} alt="RideBuraq" className="w-10 h-10 object-contain" />
            <span className="text-2xl font-bold tracking-wide text-white">Ride<span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">Buraq</span></span>
          </button>
        </header>

        <div className="pt-4 pb-6">
          <p className="text-[11px] tracking-[0.3em] uppercase text-orange-400/80 mb-2">Your Journeys</p>
          <h1 className="text-4xl font-semibold tracking-tight leading-none">Ride History</h1>
        </div>

        {/* Summary */}
        {rides.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-4">
              <p className="text-2xl font-semibold text-white">{rides.length}</p>
              <p className="text-[11px] tracking-[0.2em] uppercase text-gray-500 mt-1">Total Rides</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 py-4">
              <p className="text-2xl font-semibold text-orange-400">${totalSpent.toFixed(2)}</p>
              <p className="text-[11px] tracking-[0.2em] uppercase text-gray-500 mt-1">Total Spent</p>
            </div>
          </div>
        )}

        {/* Search + Filter */}
        <div className="flex gap-2 mb-5">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              type="text"
              data-testid="ride-search"
              placeholder="Search by location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/[0.03] border-white/10 text-white placeholder-gray-500 focus:border-orange-500 rounded-xl"
            />
          </div>
          <select
            data-testid="ride-filter"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 bg-white/[0.03] border border-white/10 text-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-500"
          >
            <option value="all" className="bg-gray-900">All</option>
            <option value="completed" className="bg-gray-900">Completed</option>
            <option value="pending" className="bg-gray-900">Pending</option>
            <option value="in-progress" className="bg-gray-900">In Progress</option>
            <option value="cancelled" className="bg-gray-900">Cancelled</option>
          </select>
        </div>

        {/* List */}
        {filteredRides.length > 0 ? (
          <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 lg:gap-4">
            {filteredRides.map((ride, i) => (
              <div
                key={ride.id || `ride-${i}`}
                data-testid="ride-card"
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:border-orange-500/30 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full ${statusDot(ride.status)}`} />
                      <MapPin className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span className="text-sm font-medium text-white truncate">{ride.pickupAddress}</span>
                    </div>
                    <div className="flex items-center gap-2 pl-[22px]">
                      <MapPin className="h-3.5 w-3.5 text-orange-400 shrink-0" />
                      <span className="text-sm text-gray-400 truncate">{ride.dropoffAddress}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-base font-semibold text-white">${Number(ride.estimatedFare || 0).toFixed(2)}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5 capitalize">{ride.status}</p>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] text-gray-500">
                  <span>{formatDate(ride.scheduledTime || ride.createdAt)}</span>
                  {ride.vehicleModel && <span className="text-orange-400/70">{ride.vehicleModel}</span>}
                </div>

                {str(ride.reference) && (
                  <button type="button" data-testid="track-ride-btn" onClick={() => navigate(`/ride/${encodeURIComponent(ride.reference)}`)}
                    className="mt-3 w-full rounded-lg border border-orange-500/30 py-2 text-xs font-medium text-orange-300 hover:bg-orange-500/[0.08]">
                    {['pending', 'assigned', 'en_route_to_pickup', 'arrived', 'on_trip'].includes(ride.status) ? 'Track ride' : 'View ride'}
                  </button>
                )}

                {ride.driver && (
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <User className="h-3.5 w-3.5 text-gray-500 shrink-0" />
                      <span className="text-sm text-gray-300 truncate">{ride.driver}</span>
                    </div>
                    <button
                      type="button"
                      data-testid="favorite-driver-btn"
                      onClick={() => handleFavorite(ride.driver)}
                      disabled={favBusy === ride.driver}
                      className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-orange-300 transition-colors disabled:opacity-40"
                      aria-label="Favorite chauffeur"
                    >
                      <Heart
                        className={`h-4 w-4 transition-colors ${favoriteDrivers.includes(ride.driver) ? 'fill-orange-500 text-orange-500' : 'text-gray-500'}`}
                      />
                      {favoriteDrivers.includes(ride.driver) ? 'Favorite' : 'Add favorite'}
                    </button>
                  </div>
                )}

                {isPaid(ride) && (
                  <div data-testid="receipt-actions" className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="receipt-download"
                      onClick={() => handleReceipt(ride, 'pdf')}
                      disabled={receiptBusy === `pdf:${ride.id}`}
                      className="flex-1 rounded-lg border border-white/10 py-2 text-xs font-medium text-gray-300 hover:border-orange-400/50 hover:text-orange-300 disabled:opacity-40"
                    >
                      {receiptBusy === `pdf:${ride.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin mx-auto" /> : 'Download receipt (PDF)'}
                    </button>
                    <button
                      type="button"
                      data-testid="receipt-email"
                      onClick={() => handleReceipt(ride, 'email')}
                      disabled={receiptBusy === `email:${ride.id}`}
                      className="flex-1 rounded-lg border border-white/10 py-2 text-xs font-medium text-gray-300 hover:border-orange-400/50 hover:text-orange-300 disabled:opacity-40"
                    >
                      {receiptBusy === `email:${ride.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin mx-auto" /> : 'Email me the receipt'}
                    </button>
                  </div>
                )}

                <CancelRideBox ride={ride} onCancelled={handleCancelled} />

                {ride.status === 'completed' && ride.driver && <RatingBox ride={ride} onRated={handleRated} />}

                {ride.status === 'completed' && (
                  ride.tipAmount > 0 ? (
                    <div data-testid="tip-added" className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between">
                      <span className="text-[11px] tracking-[0.15em] uppercase text-gray-500">Gratuity</span>
                      <span className="text-sm text-emerald-400 font-medium">${Number(ride.tipAmount).toFixed(2)} added</span>
                    </div>
                  ) : (
                    <div data-testid="tip-prompt" className="mt-3 pt-3 border-t border-white/5">
                      <p className="text-[11px] text-gray-400 mb-2">Add a gratuity for your chauffeur</p>
                      <div className="flex items-center gap-2">
                        {[15, 18, 20].map((pct) => (
                          <button
                            key={pct}
                            type="button"
                            data-testid={`tip-${pct}`}
                            onClick={() => handleTip(ride, pct)}
                            disabled={tippingId === ride.id}
                            className={`flex-1 rounded-lg border py-2 text-xs font-medium transition-all disabled:opacity-40 ${
                              pct === defaultTipPct
                                ? 'border-orange-500/50 text-orange-300 bg-orange-500/[0.08]'
                                : 'border-white/10 text-gray-400 hover:border-orange-400/50 hover:text-orange-300'
                            }`}
                          >
                            {tippingId === ride.id ? <Loader2 className="h-3.5 w-3.5 animate-spin mx-auto" /> : `${pct}% · $${((Number(ride.estimatedFare || 0) * pct) / 100).toFixed(0)}`}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-white/10 px-6 py-14 text-center">
            <Car className="h-12 w-12 text-gray-600 mx-auto mb-4" strokeWidth={1.25} />
            <p className="text-gray-400 text-sm mb-5">
              {searchTerm || filterStatus !== 'all' ? 'No matching rides found.' : 'No rides yet — your journeys will appear here.'}
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <button
                data-testid="first-ride-btn"
                onClick={() => navigate(BOOK_PATH)}
                className="text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors"
              >
                Book your first ride →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RideHistory;
